import React, {useState, useMemo} from 'react';
import ScrollArea from 'react-scrollbar';
import {Badge, Empty} from 'antd';
import {Link} from 'react-router-dom';

import {useAuth} from "../../../providers/AuthProvider";
import Opportunity from '../../../models/Opportunity';
import EmptyBanner from '../../../components/EmptyBanner';
import LoadingSpinner from "../../../components/LoadingSpinner";
import ErrorBanner from "../../../components/ErrorBanner";
import AgencyOpportunity from "../../../models/AgencyOpportunity";

const ProjectPanel = ({title, opportunities, isLoading, error, refetch, totalUpdates, lastViewed}) => {
    const daysSinceUpdated = 5

    const {auth} = useAuth();

    const [activeTab, setActiveTab] = useState("All");
    let statusOptions = Opportunity.statusOptions();
    delete statusOptions['Lost/Declined']

    const tabs = useMemo(() => {
        let opps = TB.map(AgencyOpportunity.makeMany(opportunities || []), opportunity => {
            opportunity.display_status = opportunity.displayStatus();
            opportunity.last_updated = opportunity.lastUpdated();
            return opportunity;
        });
        opps = TB.orderBy(opps, 'last_updated', 'desc')

        let statusOptions = Opportunity.statusOptions();
        delete statusOptions['Lost/Declined']
        let groupedOpps = TB.groupBy(opps, 'display_status');

        return TB.reduce(statusOptions, (result, status) => {
            result[status] = groupedOpps[status] || [];
            return result
        }, {'All': opps});
    }, [opportunities, activeTab]);


    const visibleOpps = tabs[activeTab] || [];

    // console.log(visibleOpps, 'visibleOpps')

    return (
        <div className="flex-1 w-full mb-6 sm:px-3 md:w-5/12 xl:w-4/12">
            <div className="talent-dashboard-panel">

                <div className="header-content">
                    <div>
                        <Badge count={parseInt(0) || 0}>
                            <h1 className="dashboard-title">
                                {/* Projects */}
                                {title || TB.tryTrans('talent.dashboard.project_title')}

                            </h1>
                        </Badge>

                        {opportunities?.length > 0 && (
                            <div className="header-last-updated static -mt-1.5">
                                <p className={daysSinceUpdated > 30 ? 'text-danger' : ''}>
                                    <i className="fas fa-clock"/>
                                    &nbsp; {`${daysSinceUpdated} day${(daysSinceUpdated === 1) ? '' : 's'} ago`}
                                </p>
                            </div>
                        )}
                    </div>

                    {opportunities?.length > 0 && (
                        <Link to="/supplier/projects/all" className="btn btn-primary ml-auto self-center">
                            View Projects
                        </Link>
                    )}
                </div>

                <div className="talent-panel-content-container">
                    {(!isLoading && !error && opportunities) ? (
                        opportunities?.length > 0 ? (
                            <div className="opportunity-list flex-grow">
                                <div className="opportunity-list-section flex-grow">
                                    <div className="opportunity-status-row mb-1">
                                        {TB.keys(tabs).map(status => (
                                            <button
                                                key={status}
                                                onClick={() => setActiveTab(status)}
                                                className={TB.mapClass({
                                                    "opportunity-status-tab": true,
                                                    'active': status === activeTab
                                                })}
                                            >
                                                {status}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="opportunity-list-items flex-grow">
                                        <ScrollArea
                                            speed={0.8}
                                            className="area min-h-[260px] max-h-[260px]"
                                            smoothScrolling={false}
                                            contentClassName="scroll-area"
                                            stopScrollPropagation={true}
                                            horizontal={false}>

                                            {visibleOpps.length > 0 ? visibleOpps.map(opportunity => (

                                                <div key={`opp-${opportunity.id}`}
                                                     className="opportunity-list-item"
                                                     style={{
                                                         opacity: !opportunity.isAvailable() ? 0.6 : 1,
                                                         pointerEvents: !opportunity.isAvailable() ? 'none' : 'unset'
                                                     }}
                                                >
                                                    <div className="flex flex-row justify-between items-center gap-2">
                                                        <Link to={opportunity.detailLink()}
                                                              className="mb-1 flex-grow opportunity-detail text-dark text-bold text-underline">

                                                            {opportunity.hasUnseenUpdate() && (
                                                                <span
                                                                    className="mr-1 inline-block py-px px-1.5 h-[13px] bg-danger rounded-xl text-white text-[8px]">UPDATED!</span>
                                                            )}
                                                            {opportunity.project.name}
                                                        </Link>

                                                        <p className="ml-auto text-xxs whitespace-nowrap mr-2 mt-[-13px]">
                                                            <span className="font-bold text-italic"
                                                                  style={{color: opportunity.statusColor()}}>
                                                                {opportunity.display_status}
                                                            </span>
                                                        </p>
                                                    </div>

                                                    <p className="text-xs px-2">{opportunity.project_position?.title || opportunity.project?.name}</p>

                                                    <div className="opp-item-details">
                                                        <div className="opp-item-info">
                                                            {opportunity.last_updated && (
                                                                <p className="mb-0">
                                                                    Updated: <span>
                                                                    <strong><i>{TB.moment(opportunity.last_updated)
                                                                        .fromNow()}</i></strong></span>
                                                                </p>
                                                            )}
                                                            <div className="pt-1.5 flex gap-2">
                                                                <p className="text-xxs">
                                                                    <strong>{opportunity.matching_talent_count}</strong> Matches
                                                                </p>
                                                                <p className="text-xxs">
                                                                    <strong>{opportunity.invited_candidates_count}</strong> Interested
                                                                </p>
                                                                <p className="text-xxs">
                                                                    <strong>{opportunity.interested_candidates_count}</strong> Invited
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {opportunity.canMessageAndAcceptInterviews() && (
                                                            <div className="opp-links">
                                                                {auth.canViewTalentOppMessages() && (
                                                                    <Link className="btn btn-link btn-link-info"
                                                                          to={{
                                                                              pathname: opportunity.proposalLink(),
                                                                              search: `?tabView=messages`
                                                                          }}>
                                                                        <span className="text-secondary">
                                                                            {!!opportunity.unread_messages_count && (
                                                                                <Badge
                                                                                    count={opportunity.unread_messages_count}
                                                                                    size="small" className="mr-1"/>
                                                                            )}
                                                                        </span>
                                                                        <span>
                                                                            Messages
                                                                        </span>
                                                                    </Link>
                                                                )}


                                                                {auth.canViewTalentOppInterviews() && (
                                                                    <Link className="btn btn-link btn-link-info"
                                                                          to={{
                                                                              pathname: opportunity.proposalLink(),
                                                                              search: `?tabView=interviews`
                                                                          }}>
                                                                        <span className="text-secondary">
                                                                            {!!opportunity.proposed_meetings_count && (
                                                                                <Badge
                                                                                    count={opportunity.proposed_meetings_count}
                                                                                    size="small" className="mr-1"/>
                                                                            )}
                                                                        </span>
                                                                        <span>
                                                                            Interviews
                                                                        </span>
                                                                    </Link>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>


                                                </div>
                                            )) : (
                                                <EmptyBanner message={`No ${activeTab} Projects Yet`}/>
                                            )}
                                        </ScrollArea>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <Empty
                                className="flex flex-col items-center justify-center flex-grow"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={(
                                    <div className="flex flex-col gap-2 px-3 items-center justify-center">
                                        <p className="text-lg">
                                            No Projects Yet
                                        </p>
                                        <p className="text-sm">
                                            Invite talent resources and pursue opportunities from the job board.
                                        </p>
                                    </div>
                                )}/>
                        )

                    ) : (
                        <div className="flex-grow items-center justify-center flex flex-col">
                            {isLoading ? <LoadingSpinner size={'lg'}/> : (
                                error && <ErrorBanner onRetry={refetch}/>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ProjectPanel;